import { ArrowDownWideNarrow, ArrowUpNarrowWide, Settings2 } from '@tamagui/lucide-icons';
import { hoverStyle, pressableColor } from '@unistocks/components/basic/variants';
import { Column, Row } from '@unistocks/components/layout/flex';
import { Spinner } from '@unistocks/components/loading/spinner/spinner';
import { Text, TextProps } from '@unistocks/components/text/text';
import {
  CellValue,
  PortfolioTableColumn,
  TableColumn
} from '@unistocks/features/portfolios/components/portfolio-table/types';
import {
  PortfolioDto,
  SectionAssetDto,
  SectionDto,
  SortOrder,
  SortSetting
} from '@unistocks/proxy/axios-client';
import {
  formatCurrency,
  formatDateTime,
  formatNumber,
  formatPercentage,
  t
} from '@unistocks/shared/localization/index.ts';
import {
  getFractionalDigitsOptions,
  getTotalAssetQuantity,
  isHoldings
} from '@unistocks/shared/utils/asset-utils';
import { isNumber, isToday } from '@unistocks/shared/utils/util';
import React, { useState } from 'react';
import { GestureResponderEvent } from 'react-native';
import { isWeb, Paragraph, ParagraphProps, styled, View } from 'tamagui';

const TableHeaderText = styled(Text, {
  name: 'TableHeaderText',
  size: '$1',
  fontSize: 10,
  // RN Android bug https://github.com/react/react-native/issues/56402
  // textAlign: 'right',
  verticalAlign: 'middle',
  textTransform: 'uppercase',
  numberOfLines: 1,
  userSelect: 'none',

  $md: {
    fs: '$3'
  },

  variants: {
    sortable: {
      true: {
        cursor: 'pointer',
        hoverStyle: hoverStyle,
        pressStyle: pressableColor.pressStyle
      }
    }
  }
});

type TableHeaderCellProps = Omit<ParagraphProps, 'onPress'> & {
  column: PortfolioTableColumn;
  sorting?: SortSetting;
  isLoading?: boolean;
  onPress?: (prop: string) => void;
};

export const TableHeaderCell: React.FC<TableHeaderCellProps> = ({
  column: { sortProp, size },
  sorting,
  justifyContent,
  isLoading,
  onPress,
  children,
  ...props
}) => {
  const [isPressed, setIsPressed] = useState(false);
  const sortable = !!sortProp && isWeb;
  const renderSortingIcon = (prop: string) => {
    if (sorting?.property === prop) {
      if (isLoading) {
        return <Spinner scale={0.4} />;
      }
      return sorting.sortOrder === SortOrder.Ascending ? (
        <ArrowUpNarrowWide color={'$color'} size={12} />
      ) : (
        <ArrowDownWideNarrow color={'$color'} size={12} />
      );
    }

    return null;
  };

  return (
    <Row
      flex={size}
      alignItems="center"
      justifyContent={justifyContent}
      cursor={sortable ? 'pointer' : 'default'}
      onPressIn={() => setIsPressed(true)}
      onPressOut={() => setIsPressed(false)}
      onPress={sortProp ? () => onPress?.(sortProp) : undefined}
    >
      <TableHeaderText {...props} sortable={sortable} forceStyle={isPressed ? 'press' : undefined}>
        {children}
      </TableHeaderText>
      {children && sortProp ? renderSortingIcon(sortProp) : null}
    </Row>
  );
};

export type TableCellProps = TextProps & {
  column: PortfolioTableColumn;
  asset: SectionAssetDto;
  portfolio?: PortfolioDto;
};

export const TableCellText = styled(Text, {
  name: 'TableCellText',
  size: '$1',
  fontSize: 10,
  color: '$bodyFont',
  textAlign: 'right',
  numberOfLines: 1,
  userSelect: 'none',

  $md: {
    fs: '$3'
  }
});

export const TableCell: React.FC<TableCellProps> = ({
  column: { prop, size },
  asset,
  portfolio,
  onPress,
  ...props
}) => {
  const [first, second] = getCellValue(prop, asset, portfolio);
  const color = getValueChangeColor(prop, asset);

  const onCellPress = (e: GestureResponderEvent) => {
    if (prop && onPress) {
      e.preventDefault();
      e.stopPropagation();
      onPress(e);
    }
  };

  return (
    <Column flex={size} onPress={onCellPress}>
      {first ? (
        <TableCellText textAlign="right" color={color} numberOfLines={1} {...props}>
          {first}
        </TableCellText>
      ) : null}
      {second ? (
        <TableCellText textAlign="right" color={color} numberOfLines={1} {...props}>
          {second}
        </TableCellText>
      ) : null}
      {prop === 'settings' ? (
        <Settings2
          color={'$unistocks'}
          /* y={-6} */
          ml={props.textAlign === 'right' ? 'auto' : undefined}
          mr={props.textAlign === 'left' ? 'auto' : undefined}
          mx={props.textAlign === 'center' ? 'auto' : undefined}
        />
      ) : null}
    </Column>
  );
};

export type TableFooterCellProps = TextProps & {
  column: PortfolioTableColumn;
  section: SectionDto;
  portfolio: PortfolioDto;
};

export const TableFooterCell: React.FC<TableFooterCellProps> = ({
  column: { prop, size },
  section,
  portfolio,
  ...props
}) => {
  const value = getFooterCellValue(prop, section, portfolio);
  const color = getFooterValueChangeColor(prop, section);

  return (
    <View flex={size}>
      <TableCellText color={color} numberOfLines={1} fw={'$7'} textAlign="right" {...props}>
        {value}
      </TableCellText>
    </View>
  );
};

export const TableDetailsCell: React.FC<TableCellProps> = ({
  column: { prop, displayName },
  asset,
  portfolio,
  ...props
}) => {
  const [first, second] = getCellValue(prop, asset, portfolio);

  return first || second ? (
    <Column mx={'$3'} gap={'$2'}>
      <TableHeaderText
        o={0.75}
        textAlign="center"
        borderBottomColor={'$borderColor'}
        borderBottomWidth={1}
        userSelect="none"
      >
        {displayName}
      </TableHeaderText>
      {first ? (
        <Paragraph
          size={'$1'}
          color={getValueChangeColor(prop, asset)}
          numberOfLines={1}
          textAlign="right"
          userSelect="none"
          {...props}
        >
          {first}
        </Paragraph>
      ) : null}
      {second ? (
        <Paragraph
          size={'$1'}
          color={getValueChangeColor(prop, asset)}
          textAlign="right"
          numberOfLines={1}
          userSelect="none"
          {...props}
        >
          {second}
        </Paragraph>
      ) : null}
    </Column>
  ) : null;
};

TableHeaderCell.displayName = 'TableHeaderCell';
TableCell.displayName = 'TableCell';
TableFooterCell.displayName = 'TableFooterCell';
TableDetailsCell.displayName = 'TableDetailsCell';

/**
 * Returns the cell value for a given asset and column.
 * @returns Array of the cell value of the first and second row
 */
const getCellValue = (
  column: TableColumn,
  asset: SectionAssetDto,
  portfolio?: PortfolioDto
): [CellValue, CellValue] => {
  let value: number | undefined, valueChange: number | null, quantity: number | null;

  switch (column) {
    case 'name':
      return [
        portfolio && isNumber(asset.currentPrice)
          ? formatCurrency(asset.currentPrice, asset.currency)
          : '',
        ''
      ];

    case 'currentPrice':
      return [
        isNumber(asset.currentPrice) ? formatCurrency(asset.currentPrice, asset.currency) : '',
        ''
      ];

    case 'currentPriceChange':
      return [
        isNumber(asset.percentageChange)
          ? formatPercentage(asset.percentageChange, { signDisplay: 'exceptZero' })
          : '',
        isNumber(asset.valueChange)
          ? formatCurrency(asset.valueChange, asset.currency, {
              signDisplay: 'exceptZero',
              maximumFractionDigits: Math.abs(asset.valueChange) < 100 ? 2 : 0
            })
          : ''
      ];

    case 'totalChange':
      return [
        isNumber(asset.totalChangePercent)
          ? formatPercentage(asset.totalChangePercent, {
              signDisplay: 'exceptZero'
            })
          : '',
        isNumber(asset.totalChange) && portfolio
          ? formatCurrency(asset.totalChange, portfolio.currency, {
              signDisplay: 'exceptZero',
              maximumFractionDigits: 0
            })
          : ''
      ];

    case 'calculatedValue':
      quantity = getTotalAssetQuantity(asset);

      const first =
        portfolio && isNumber(asset.calculatedValue)
          ? formatCurrency(asset.calculatedValue, portfolio.currency, {
              maximumFractionDigits: Math.abs(asset.calculatedValue) < 100 ? 2 : 0
            })
          : '';

      let second: CellValue = '';

      if (isHoldings(asset)) {
        second = isNumber(quantity)
          ? `${formatNumber(quantity, getFractionalDigitsOptions(asset))} ${t('::QuantitySuffix')}`
          : '';
      } else {
        second =
          asset.currency !== portfolio?.currency && isNumber(asset.totalValue)
            ? formatCurrency(asset.totalValue, asset.currency)
            : '';
      }

      return [first, second];

    case 'calculatedValueShare':
      return [
        portfolio ? formatPercentage(asset.calculatedValue / portfolio.calculatedValue) : '',
        ''
      ];

    case 'averageAcquisitionPrice':
      if (
        isNumber(asset.calculatedAverageAcquisitionPrice?.value) &&
        isNumber(asset.calculatedAcquisitionValue?.value) &&
        portfolio
      ) {
        const fractionalDigitsOptions = asset.calculatedAverageAcquisitionPrice.value < 100 ? 2 : 0;

        return [
          formatCurrency(
            asset.calculatedAverageAcquisitionPrice.value,
            asset.calculatedAverageAcquisitionPrice.currency,
            {
              maximumFractionDigits: fractionalDigitsOptions
            }
          ),
          formatCurrency(
            asset.calculatedAcquisitionValue.value,
            asset.calculatedAcquisitionValue.currency,
            {
              maximumFractionDigits: 0
            }
          )
        ];
      }
      return ['', ''];

    case 'averageAcquisitionPriceAssetCurrency':
      if (
        isNumber(asset.calculatedAverageAcquisitionPriceAssetCurrency?.value) &&
        isNumber(asset.calculatedAcquisitionValueAssetCurrency?.value) &&
        portfolio &&
        portfolio.currency !== asset.calculatedAverageAcquisitionPriceAssetCurrency.currency
      ) {
        const fractionalDigitsOptions =
          asset.calculatedAverageAcquisitionPriceAssetCurrency.value < 100 ? 2 : 0;

        return [
          formatCurrency(
            asset.calculatedAverageAcquisitionPriceAssetCurrency.value,
            asset.calculatedAverageAcquisitionPriceAssetCurrency.currency,
            {
              maximumFractionDigits: fractionalDigitsOptions
            }
          ),
          formatCurrency(
            asset.calculatedAcquisitionValueAssetCurrency.value,
            asset.calculatedAcquisitionValueAssetCurrency.currency,
            {
              maximumFractionDigits: 0
            }
          )
        ];
      }
      return ['', ''];

    case 'OneWeek':
    case 'OneMonth':
    case 'ThreeMonths':
    case 'SixMonths':
    case 'ThisYear':
    case 'OneYear':
    case 'ThreeYears':
    case 'FiveYears':
      value = asset.historicalPrices?.[column];
      valueChange =
        isNumber(value) && isNumber(asset.currentPrice) ? asset.currentPrice - value : null;
      return isNumber(valueChange)
        ? [formatPercentage(valueChange / value!), formatCurrency(valueChange, asset.currency)]
        : ['', ''];

    case 'updated':
      return asset.updated
        ? [
            formatDateTime(asset.updated, {
              month: 'long',
              day: 'numeric',
              hour: 'numeric',
              minute: 'numeric'
            }),
            ''
          ]
        : ['', ''];

    default:
      return ['', ''];
  }
};

const getValueChangeColor = (column: TableColumn, asset: SectionAssetDto): TextProps['color'] => {
  switch (column) {
    case 'currentPriceChange':
      return isToday(asset.updated)
        ? isNumber(asset.valueChange)
          ? asset.valueChange > 0
            ? '$positive'
            : asset.valueChange < 0
              ? '$negative'
              : '$bodyFont'
          : '$bodyFont'
        : '$gray9';

    case 'totalChange':
      return isNumber(asset.totalChange)
        ? asset.totalChange > 0
          ? '$positive'
          : asset.totalChange < 0
            ? '$negative'
            : '$bodyFont'
        : '$gray9';

    case 'OneWeek':
    case 'OneMonth':
    case 'ThreeMonths':
    case 'SixMonths':
    case 'ThisYear':
    case 'OneYear':
    case 'ThreeYears':
    case 'FiveYears':
      const value = asset.historicalPrices?.[column];
      const valueChange =
        isNumber(value) && isNumber(asset.currentPrice) ? asset.currentPrice - value : null;
      return isNumber(valueChange)
        ? valueChange > 0
          ? '$positive'
          : valueChange < 0
            ? '$negative'
            : '$bodyFont'
        : '$gray9';

    default:
      return '$bodyFont';
  }
};

const getFooterCellValue = (
  column: TableColumn,
  section: SectionDto,
  portfolio: PortfolioDto
): CellValue => {
  switch (column) {
    case 'currentPriceChange':
      return isNumber(section.percentageChange) ? formatPercentage(section.percentageChange) : '';

    case 'totalChange':
      return isNumber(section.totalChangePercent)
        ? formatPercentage(section.totalChangePercent)
        : '';

    case 'calculatedValue':
      return isNumber(section.calculatedValue)
        ? formatCurrency(section.calculatedValue, portfolio.currency, { maximumFractionDigits: 0 })
        : '';

    case 'calculatedValueShare':
      return isNumber(section.calculatedValue) && isNumber(portfolio.calculatedValue)
        ? formatPercentage(section.calculatedValue / portfolio.calculatedValue)
        : '';

    default:
      return '';
  }
};

const getFooterValueChangeColor = (
  column: TableColumn,
  section: SectionDto
): TextProps['color'] => {
  switch (column) {
    case 'currentPriceChange':
      return isNumber(section.percentageChange)
        ? section.percentageChange > 0
          ? '$positive'
          : section.percentageChange < 0
            ? '$negative'
            : '$bodyFont'
        : '$gray9';

    case 'totalChange':
      return isNumber(section.totalChange)
        ? section.totalChange > 0
          ? '$positive'
          : section.totalChange < 0
            ? '$negative'
            : '$bodyFont'
        : '$gray9';

    default:
      return '$bodyFont';
  }
};
